import { Phone, Mail, MessageCircle, MapPin, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

// HubSpot Forms API type declaration
declare global {
  interface Window {
    hbspt?: {
      forms: {
        create: (options: {
          region: string;
          portalId: string;
          formId: string;
          target: string;
        }) => void;
      };
    };
  }
}

const Contact = () => {
  const { t, i18n } = useTranslation();

  // Form IDs based on language
  const formId = i18n.language === 'es' 
    ? "3c82c746-37aa-4031-a7c1-982d057be0f8"  // Spanish form
    : "4f1fe520-fd15-49b4-987c-1c1c212e33e4"; // English form

  // New-editor English forms use the portal loader; Spanish keeps its existing embed.
  useEffect(() => {
    if (formId === "4f1fe520-fd15-49b4-987c-1c1c212e33e4") {
      const src = "https://js.hsforms.net/forms/embed/50706037.js";
      if (!document.querySelector(`script[src="${src}"]`)) {
        const script = document.createElement("script");
        script.src = src;
        script.defer = true;
        document.body.appendChild(script);
      }
      return;
    }

    const container = document.getElementById('hubspot-form-container');
    if (!container) return;
    container.innerHTML = '';
    let cancelled = false;

    const createForm = () => {
      if (!cancelled && container.isConnected && window.hbspt) {
        window.hbspt.forms.create({
          region: "na1",
          portalId: "50706037",
          formId: formId,
          target: "#hubspot-form-container"
        });
      }
    };

    // Check if script is already loaded
    const existingScript = document.querySelector('script[src="https://js.hsforms.net/forms/embed/v2.js"]');
    
    if (window.hbspt) {
      createForm();
    } else if (existingScript) {
      existingScript.addEventListener('load', createForm, { once: true });
    } else if (!existingScript) {
      // Load script for the first time
      const script = document.createElement('script');
      script.src = 'https://js.hsforms.net/forms/embed/v2.js';
      script.async = true;
      script.charset = 'utf-8';
      script.addEventListener('load', createForm, { once: true });
      document.body.appendChild(script);
    }
    return () => {
      cancelled = true;
      existingScript?.removeEventListener('load', createForm);
    };
  }, [formId]);

  return (
    <section id="contact" className="py-16 sm:py-20 md:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-14 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 px-2">{t('contact.title')}</h2>
          <div className="w-16 sm:w-20 h-1 bg-primary mx-auto mb-4 sm:mb-6"></div>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 max-w-6xl mx-auto">
          {/* Contact Methods */}
          <div className="space-y-4 sm:space-y-6 md:space-y-8">
            <a
              href="https://wa.me/50683541716"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 sm:gap-6 p-4 sm:p-6 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors group"
              aria-label="Contact us via WhatsApp at +506 8354-1716"
            >
              <div className="p-3 sm:p-4 bg-primary rounded-full group-hover:scale-110 transition-transform">
                <MessageCircle className="w-6 sm:w-8 h-6 sm:h-8 text-white" aria-hidden="true" />
              </div>
              <div>
                <div className="text-xs sm:text-sm font-semibold text-foreground uppercase tracking-wide mb-1">
                  {t('contact.whatsapp')}
                </div>
                <div className="text-lg sm:text-xl font-bold text-foreground">+506 8354-1716</div>
              </div>
            </a>

            <a
              href="tel:+18336484745"
              className="flex items-center gap-4 sm:gap-6 p-4 sm:p-6 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors group"
              aria-label="Call toll-free number +1 833-648-4745"
            >
              <div className="p-3 sm:p-4 bg-primary rounded-full group-hover:scale-110 transition-transform">
                <Phone className="w-6 sm:w-8 h-6 sm:h-8 text-white" aria-hidden="true" />
              </div>
              <div>
                <div className="text-xs sm:text-sm font-semibold text-foreground uppercase tracking-wide mb-1">
                  {t('contact.tollFree')}
                </div>
                <div className="text-lg sm:text-xl font-bold text-foreground">+1 833-648-4745</div>
              </div>
            </a>

            <a
              href="tel:+50626702400"
              className="flex items-center gap-4 sm:gap-6 p-4 sm:p-6 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors group"
              aria-label="Call local number +506 2670-2400"
            >
              <div className="p-3 sm:p-4 bg-primary rounded-full group-hover:scale-110 transition-transform">
                <Phone className="w-6 sm:w-8 h-6 sm:h-8 text-white" aria-hidden="true" />
              </div>
              <div>
                <div className="text-xs sm:text-sm font-semibold text-foreground uppercase tracking-wide mb-1">
                  {t('contact.phoneLabel')}
                </div>
                <div className="text-lg sm:text-xl font-bold text-foreground">+506 2670-2400</div>
              </div>
            </a>

            <a
              href="mailto:internationalpatients@lsdentalcostarica.com"
              className="flex items-center gap-4 sm:gap-6 p-4 sm:p-6 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors group"
              aria-label="Email us at internationalpatients@lsdentalcostarica.com"
            >
              <div className="p-3 sm:p-4 bg-primary rounded-full group-hover:scale-110 transition-transform">
                <Mail className="w-6 sm:w-8 h-6 sm:h-8 text-white" aria-hidden="true" />
              </div>
              <div>
                <div className="text-xs sm:text-sm font-semibold text-foreground uppercase tracking-wide mb-1">
                  {t('contact.emailLabel')}
                </div>
                <div className="text-lg sm:text-xl font-bold text-foreground break-all">internationalpatients@lsdentalcostarica.com</div>
              </div>
            </a>

            <div
              className="flex items-center gap-4 sm:gap-6 p-4 sm:p-6 bg-secondary/50 rounded-lg group"
            >
              <div className="p-3 sm:p-4 bg-primary rounded-full group-hover:scale-110 transition-transform">
                <MapPin className="w-6 sm:w-8 h-6 sm:h-8 text-white" aria-hidden="true" />
              </div>
              <div>
                <div className="text-xs sm:text-sm font-semibold text-foreground uppercase tracking-wide mb-1">
                  {t('contact.location')}
                </div>
                <div className="text-sm sm:text-base font-medium text-foreground">{t('contact.locationValue')}</div>
              </div>
            </div>

            <div
              className="flex items-center gap-4 sm:gap-6 p-4 sm:p-6 bg-secondary/50 rounded-lg group"
            >
              <div className="p-3 sm:p-4 bg-primary rounded-full group-hover:scale-110 transition-transform">
                <Clock className="w-6 sm:w-8 h-6 sm:h-8 text-white" aria-hidden="true" />
              </div>
              <div>
                <div className="text-xs sm:text-sm font-semibold text-foreground uppercase tracking-wide mb-1">
                  {t('contact.info.hours')}
                </div>
                <div className="text-sm sm:text-base font-medium text-foreground">{t('contact.info.hoursValue')}</div>
              </div>
            </div>
          </div>

          {/* HubSpot Contact Form */}
          <div className="bg-secondary/30 p-4 sm:p-6 md:p-8 rounded-lg">
            {formId === "4f1fe520-fd15-49b4-987c-1c1c212e33e4" ? (
              <div
                key={formId}
                className="hs-form-frame"
                data-region="na1"
                data-form-id={formId}
                data-portal-id="50706037"
              />
            ) : (
              <div key={formId} id="hubspot-form-container" />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
